import React from 'react';
import { ArrowLeft, ChevronRight, LayoutGrid, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useGetPracticeQuestionTopics, useUpdateBadges } from '../../../queries';
import LoggedInUserWrapper from '../../core/LoggedInUserWrapper';
import { Skeleton } from '../../ui/skeleton';
import { getSubjectColor } from '../../Activity/utils/subjectColors';

const PAGE_COPY = {
  'practice-questions': {
    heading: 'Choose a Topic to Practice',
    subheading: 'Select a topic to practice adaptive questions'
  },
  'ask-me-questions': {
    heading: 'Choose a Topic to Ask',
    subheading: 'Select a topic to ask questions about'
  },
  'learn-a-new-topic': {
    heading: 'Choose a Topic to Learn',
    subheading: 'Select a topic to start learning'
  }
};

const DIFFICULTY_STYLES = {
  Beginner: { bg: '#dcfce7', text: '#008236' },
  Intermediate: { bg: '#fef9c2', text: '#a65f00' },
  Advanced: { bg: '#ffe2e2', text: '#c10007' }
};

const SECTION_BACKGROUNDS = [
  '#f0f8ff',
  '#f0fff0',
  '#f8f0ff',
  '#fff0f5',
  '#fff8f0',
  '#f0ffff',
  '#f0fff8',
  '#fff0f0',
  '#f8f8f0',
  '#f0f0f8'
];

const ALL_STANDARD_ID = 'all';

function slugify(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-');
}

function normalizeDifficulty(value) {
  if (!value) return null;
  const normalized = String(value).toLowerCase();
  if (normalized.includes('begin')) return 'Beginner';
  if (normalized.includes('inter')) return 'Intermediate';
  if (normalized.includes('advanc') || normalized.includes('hard')) return 'Advanced';
  return null;
}

function normalizeTopic(topic) {
  const standard = topic.standard?.trim() || 'General';
  const subject = topic.subject?.trim() || standard || 'General';
  return {
    id: topic._id,
    name: topic.topic || topic.name || 'Untitled Topic',
    description: topic.description || topic.topicDescription || '',
    difficulty: normalizeDifficulty(topic.difficulty),
    subject,
    standard
  };
}

function buildCatalog(topics) {
  const items = (Array.isArray(topics) ? topics : []).map(normalizeTopic);
  const standardMap = new Map();

  items.forEach((topic) => {
    if (!standardMap.has(topic.standard)) {
      standardMap.set(topic.standard, {
        id: slugify(topic.standard),
        name: topic.standard
      });
    }
  });

  return {
    topics: items,
    standards: Array.from(standardMap.values())
  };
}

function groupTopicsBySubject(topics) {
  const groups = new Map();

  topics.forEach((topic) => {
    if (!groups.has(topic.subject)) {
      groups.set(topic.subject, {
        id: slugify(topic.subject),
        name: topic.subject,
        meta: getSubjectColor(topic.subject),
        topics: []
      });
    }
    groups.get(topic.subject).topics.push(topic);
  });

  return Array.from(groups.values());
}

function DifficultyBadge({ level }) {
  if (!level || !DIFFICULTY_STYLES[level]) return null;
  const { bg, text } = DIFFICULTY_STYLES[level];
  return (
    <span
      className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium leading-[14px]"
      style={{ backgroundColor: bg, color: text }}
    >
      {level}
    </span>
  );
}

function TopicRow({ topic, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-[10px] border border-[#e9ecef] bg-white px-3 py-2.5 text-left shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition-colors hover:bg-white/90"
    >
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex flex-wrap items-center gap-2">
          <span className="text-[13px] font-semibold leading-snug text-[#212529]">{topic.name}</span>
          <DifficultyBadge level={topic.difficulty} />
        </div>
        <p className="text-xs leading-snug text-[#6c757d]">
          {topic.description || 'Practice questions and learn new concepts'}
        </p>
      </div>
      <ChevronRight className="h-5 w-5 shrink-0 text-[#6c757d]" strokeWidth={1.8} />
    </button>
  );
}

function SubjectGroupCard({ group, index, onTopicClick }) {
  const Icon = group.meta.Icon;
  const background = SECTION_BACKGROUNDS[index % SECTION_BACKGROUNDS.length];

  return (
    <section className="rounded-xl p-4" style={{ backgroundColor: background }}>
      <div className="mb-4 flex items-center gap-2.5">
        <span
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
          style={{ backgroundColor: group.meta.iconBg }}
        >
          {Icon ? (
            <Icon className="h-4 w-4" style={{ color: group.meta.iconColor }} strokeWidth={1.8} />
          ) : null}
        </span>
        <div>
          <p className="text-sm font-bold" style={{ color: group.meta.textColor }}>
            {group.name}
          </p>
          <p className="text-[13px] text-[#6c757d]">
            {group.topics.length} topic{group.topics.length === 1 ? '' : 's'} available
          </p>
        </div>
      </div>
      <div className="space-y-3">
        {group.topics.map((topic) => (
          <TopicRow key={topic.id} topic={topic} onClick={() => onTopicClick(topic)} />
        ))}
      </div>
    </section>
  );
}

function StandardPills({ standards, activeId, onSelect }) {
  return (
    <div className="flex w-max gap-2">
      <button
        type="button"
        onClick={() => onSelect(ALL_STANDARD_ID)}
        className={`flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 py-2 text-xs font-medium transition-all ${
          activeId === ALL_STANDARD_ID
            ? 'border-[#6f42c1] bg-[#6f42c1] text-white'
            : 'border-[#6f42c1] bg-white text-[#6f42c1]'
        }`}
      >
        <LayoutGrid className="h-4 w-4" strokeWidth={1.8} />
        All Topics
      </button>
      {standards.map((standard) => {
        const active = standard.id === activeId;
        return (
          <button
            key={standard.id}
            type="button"
            onClick={() => onSelect(standard.id)}
            className={`whitespace-nowrap rounded-full border px-3.5 py-2 text-xs font-medium transition-all ${
              active
                ? 'border-[#6f42c1] bg-[#6f42c1] text-white'
                : 'border-[#e9ecef] bg-white text-[#495057]'
            }`}
          >
            {standard.name}
          </button>
        );
      })}
    </div>
  );
}

const ChatTopicSelection = ({ title, navigationPath, heading, subheading }) => {
  const navigate = useNavigate();
  const { data, isLoading } = useGetPracticeQuestionTopics();
  const { mutate: updateBadges } = useUpdateBadges();
  const [searchQuery, setSearchQuery] = React.useState('');
  const [activeStandardId, setActiveStandardId] = React.useState(ALL_STANDARD_ID);

  const copy = PAGE_COPY[navigationPath] || {};
  const pageHeading = heading || copy.heading || title;
  const pageSubheading = subheading || copy.subheading || 'Select a topic below to begin';

  React.useEffect(() => {
    updateBadges();
  }, [updateBadges]);

  const { topics, standards } = React.useMemo(() => buildCatalog(data), [data]);

  React.useEffect(() => {
    if (activeStandardId === ALL_STANDARD_ID) return;
    if (!standards.some((standard) => standard.id === activeStandardId)) {
      setActiveStandardId(ALL_STANDARD_ID);
    }
  }, [standards, activeStandardId]);

  const groupedSubjects = React.useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const selectedStandard = standards.find((standard) => standard.id === activeStandardId)?.name;

    const filtered = topics.filter((topic) => {
      if (activeStandardId !== ALL_STANDARD_ID && selectedStandard && topic.standard !== selectedStandard) {
        return false;
      }
      if (!query) return true;
      return (
        topic.name.toLowerCase().includes(query) ||
        topic.subject.toLowerCase().includes(query) ||
        topic.standard.toLowerCase().includes(query) ||
        topic.description.toLowerCase().includes(query)
      );
    });

    return groupTopicsBySubject(filtered);
  }, [topics, standards, activeStandardId, searchQuery]);

  const onSelectTopic = (topic) => {
    if (topic?.id) {
      navigate(`/${navigationPath}/${topic.id}`);
    }
  };

  const onBack = () => navigate('/');
  const topicCount = groupedSubjects.reduce((sum, group) => sum + group.topics.length, 0);

  const topicList = isLoading ? (
    <div className="space-y-4">
      <Skeleton className="h-40 w-full rounded-xl" />
      <Skeleton className="h-40 w-full rounded-xl" />
    </div>
  ) : groupedSubjects.length === 0 ? (
    <div className="flex flex-col items-center justify-center rounded-xl bg-white py-16 text-center">
      <Search className="mb-3 h-10 w-10 text-gray-300" />
      <p className="text-sm font-medium text-gray-500">
        {searchQuery ? 'No topics found' : 'No topics available'}
      </p>
      <p className="mt-1 max-w-sm text-xs text-gray-400">
        {searchQuery
          ? 'Try a different search term or another standard.'
          : 'Check back later for new learning materials.'}
      </p>
    </div>
  ) : (
    groupedSubjects.map((group, index) => (
      <SubjectGroupCard
        key={group.id}
        group={group}
        index={index}
        onTopicClick={onSelectTopic}
      />
    ))
  );

  return (
    <LoggedInUserWrapper>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-white">
        <div
          className="flex min-h-0 flex-1 flex-col overflow-hidden md:hidden"
          style={{
            background:
              'linear-gradient(135deg, #f9fafb 0%, rgba(239,246,255,0.5) 60%, rgba(238,242,255,0.4) 100%)'
          }}
        >
          <div className="shrink-0 px-4 pb-3 pt-4">
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={onBack}
                className="-ml-1.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-[#4a5565] transition-colors hover:bg-black/5 hover:text-gray-900"
                aria-label="Back"
              >
                <ArrowLeft className="h-4 w-4" strokeWidth={2} />
              </button>
              <h1 className="min-w-0 flex-1 text-[17.5px] font-bold leading-snug text-gray-900">
                {pageHeading}
              </h1>
            </div>
            <p className="mt-0.5 pl-[1.875rem] text-[12.25px] text-[#4a5565]">{pageSubheading}</p>
          </div>

          <div className="shrink-0 px-4 pb-3">
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#99A1AF]"
                strokeWidth={1.5}
              />
              <input
                type="text"
                placeholder="Search topics across all goals..."
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                className="w-full rounded-lg border border-gray-200/50 bg-white/70 py-2 pl-8 pr-3 text-[14px] outline-none transition-all placeholder:text-[#717182] focus:border-primary/30 focus:ring-2 focus:ring-primary/20"
              />
            </div>
          </div>

          {!isLoading && standards.length > 0 ? (
            <div className="shrink-0 overflow-x-auto px-4 pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <StandardPills
                standards={standards}
                activeId={activeStandardId}
                onSelect={setActiveStandardId}
              />
            </div>
          ) : null}

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-8">
            <div className="space-y-5">{topicList}</div>
          </div>
        </div>

        <div className="hidden min-h-0 flex-1 bg-white md:flex">
          <div className="flex h-full min-h-0 w-72 shrink-0 flex-col overflow-hidden border-r border-gray-100 bg-gray-50/60">
            <div className="shrink-0 px-6 pb-5 pt-8">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onBack}
                  className="-ml-2 flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-[#4a5565] transition-colors hover:bg-black/5 hover:text-gray-900"
                  aria-label="Back"
                >
                  <ArrowLeft className="h-4 w-4" strokeWidth={2} />
                </button>
                <h1 className="min-w-0 flex-1 text-lg font-bold leading-snug text-gray-900">
                  {pageHeading}
                </h1>
              </div>
              <p className="mt-1 pl-11 text-sm text-gray-500">{pageSubheading}</p>
            </div>

            <div className="mb-4 shrink-0 px-4">
              <div className="relative">
                <Search
                  className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                  strokeWidth={1.5}
                />
                <input
                  type="text"
                  placeholder="Search topics..."
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm outline-none transition-all focus:border-primary/30 focus:ring-2 focus:ring-primary/20"
                />
              </div>
            </div>

            <nav className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 pb-6">
              {isLoading
                ? Array.from({ length: 5 }).map((_, index) => (
                    <Skeleton key={index} className="mb-1 h-12 w-full rounded-xl" />
                  ))
                : (
                    <>
                      <button
                        type="button"
                        onClick={() => setActiveStandardId(ALL_STANDARD_ID)}
                        className="mb-0.5 flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-all"
                        style={
                          activeStandardId === ALL_STANDARD_ID
                            ? { backgroundColor: '#f3e8ff' }
                            : undefined
                        }
                      >
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#f3e8ff] text-[#6f42c1]">
                          <LayoutGrid className="h-4 w-4" strokeWidth={1.8} />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p
                            className="truncate text-sm font-medium"
                            style={{ color: activeStandardId === ALL_STANDARD_ID ? '#6f42c1' : '#374151' }}
                          >
                            All Topics
                          </p>
                          <p className="text-xs text-gray-400">
                            {topics.length} {topics.length === 1 ? 'topic' : 'topics'}
                          </p>
                        </div>
                      </button>
                      {standards.map((standard) => {
                        const active = standard.id === activeStandardId;
                        const count = topics.filter((topic) => topic.standard === standard.name).length;
                        return (
                          <button
                            key={standard.id}
                            type="button"
                            onClick={() => setActiveStandardId(standard.id)}
                            className="mb-0.5 flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-all"
                            style={active ? { backgroundColor: '#f3e8ff' } : undefined}
                          >
                            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-500">
                              <LayoutGrid className="h-4 w-4" strokeWidth={1.8} />
                            </span>
                            <div className="min-w-0 flex-1">
                              <p
                                className="truncate text-sm font-medium"
                                style={{ color: active ? '#6f42c1' : '#374151' }}
                              >
                                {standard.name}
                              </p>
                              <p className="text-xs text-gray-400">
                                {count} {count === 1 ? 'topic' : 'topics'}
                              </p>
                            </div>
                            {active ? (
                              <ChevronRight className="h-4 w-4 shrink-0 text-[#6f42c1]" strokeWidth={2} />
                            ) : null}
                          </button>
                        );
                      })}
                    </>
                  )}
            </nav>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-8 py-8">
            {searchQuery ? (
              <p className="mb-6 text-sm text-gray-500">
                {topicCount === 0
                  ? `No results for "${searchQuery}"`
                  : `${topicCount} topic${topicCount === 1 ? '' : 's'} matching "${searchQuery}"`}
              </p>
            ) : null}
            <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">{topicList}</div>
          </div>
        </div>
      </div>
    </LoggedInUserWrapper>
  );
};

export default ChatTopicSelection;
